import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { api, authHeaders, fmtError } from "@/lib/api";

export const RED = "#4A0E17";


export function uploadImage(file) {
  const form = new FormData();
  form.append("file", file);
  return axios
    .post(api("/api/admin/upload"), form, {
      headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data.url);
}

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase font-bold tracking-wider mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-black/50 mt-1">{hint}</span>}
    </label>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      className={`w-full bg-white border-2 border-black/15 focus:border-black px-3 py-2 text-base outline-none transition-colors ${props.className || ""}`}
    />
  );
}

export function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full bg-white border-2 border-black/15 focus:border-black px-3 py-2 text-base outline-none transition-colors resize-y ${props.className || ""}`}
    />
  );
}

export function PrimaryBtn({ children, ...props }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center gap-2 px-4 py-2 text-white font-bold text-sm uppercase tracking-wider rounded-full disabled:opacity-50 ${props.className || ""}`}
      style={{ background: RED, ...(props.style || {}) }}
    >
      {children}
    </button>
  );
}

export function SecondaryBtn({ children, ...props }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center gap-2 px-4 py-2 font-bold text-sm uppercase tracking-wider rounded-full border border-black/20 hover:bg-black hover:text-white transition-colors ${props.className || ""}`}
    >
      {children}
    </button>
  );
}

export function ImageUploadField({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const url = await uploadImage(f);
      onChange(url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="space-y-2">
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste any image URL or upload below" />
      <div className="flex items-center gap-3">
        <label className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-full border border-black/20 cursor-pointer hover:bg-black hover:text-white">
          <Upload size={12} /> {busy ? "Uploading…" : "Upload file"}
          <input type="file" accept="image/*,.heic,.heif" className="hidden" onChange={onFile} disabled={busy} data-testid="image-upload-field-input" />
        </label>
        {value && (
          <img src={value} alt="preview" className="h-16 w-auto object-cover border border-black/15" />
        )}
      </div>
    </div>
  );
}

export function FileUploadField({ value, onChange, accept = ".pdf,application/pdf" }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      onChange(await uploadImage(f));
      toast.success("File uploaded. Press Save to publish it.");
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="space-y-2">
      <Input data-testid="file-upload-url" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Upload a PDF or paste a link" />
      <div className="flex items-center gap-3">
        <label data-testid="file-upload-btn" className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-full border border-black/20 cursor-pointer hover:bg-black hover:text-white">
          <Upload size={12} /> {busy ? "Uploading…" : "Upload PDF"}
          <input type="file" accept={accept} className="hidden" onChange={onFile} disabled={busy} />
        </label>
        {value && <a href={value.startsWith("/") ? api(value) : value} target="_blank" rel="noreferrer" data-testid="file-upload-preview" className="text-xs font-bold underline">Open current file</a>}
      </div>
    </div>
  );
}
